// DOCUMENTATION
// https://echarts.apache.org/en/option.html
// https://echarts.apache.org/en/api.html#echarts
import * as echarts from "echarts/core";
import { BarChart, LineChart, GaugeChart, GraphChart, PieChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
// ADD HERE: NEW CHART types
import { options as esg_gauge_and_donut } from "./esg_gauge";
import { options as compliance_donut } from "./compliance_donut";
import { options as esg_bars } from "./esg_bars";
import { options as esg_line } from "./esg_line";

const debugLifecycle = false;

echarts.use([
  BarChart,
  GridComponent,
  LegendComponent,
  LineChart,
  GaugeChart,
  GraphChart,
  PieChart,
  SVGRenderer,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
]);

const init = () => {
  customElements.define(
    "echart-elem",
    class extends HTMLElement {
      constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.shadowRoot.innerHTML = `<div id="container" style="direction: ltr; width: 100%; height: 100%;"></div>`; //TODO remove "direction: ltr" when Echarts supports rtl
        if (debugLifecycle) {
          console.log("echarts constructor");
        }
      }

      //TODO add out-bound events

      connectedCallback() {
        if (debugLifecycle) {
          console.log("echarts connectedCallback");
        }
        if (!this.isConnected) {
          //NOTE: connectedCallback CAN be called after disconnectedCallback, so check.
          if (debugLifecycle) {
            console.log("echarts NOT CONNECTED. Returning");
          }
          return;
        }

        setTimeout(() => {
          this.updateChart(); //NOTE: connectedCallback can trigger before DOM is ready, so use setTimeout to delay until rendering is possible
        });

        window.addEventListener("resize", () => this.resize());
      }

      disconnectedCallback() {
        if (debugLifecycle) {
          console.log("echarts disconnected");
        }
        if (this.chart && this.chart.dispose) {
          this.chart.dispose();
          this.chart = null;
        }
        this._data = null;
        window.removeEventListener("resize", () => this.resize());
        const container = this.shadowRoot.querySelector("#container");
        if (container) {
          container.innerHTML = "";
        }
      }

      static get observedAttributes() {
        return ["json"];
      }

      attributeChangedCallback(name, oldValue, _newValue) {
        // console.log("echarts attributeChangedCallback", name, oldValue, newValue);
        if (name === "json" && oldValue != null) {
          //NOTE: this skips the first time that an attribute is set before it has been rendered for the 1st time.
          setTimeout(() => {
            if (debugLifecycle) {
              console.log("echarts trigger render");
            }
            this.updateChart();
          });
        }
      }

      updateChart() {
        if (debugLifecycle) {
          console.log("echarts updateChart()");
        }
        if (!this.chart) {
          if (debugLifecycle) {
            console.log("echarts about to init");
          }
          const container = this.shadowRoot.querySelector("#container");
          this.chart = echarts.init(container, null, { renderer: "svg" });
          // NOTE 1: if echarts.init called in the constructor (instead of here) can cause a "Can't get dom width or height" warning in the console log
          // NOTE 2: echarts throws the same warning if a containing div is display:none. https://github.com/apache/echarts/issues/10478
          // Our media query on page-load to display/hide the accordion triggers the warning, because the display:none elements are still added, causing
          // echarts to complain https://stackoverflow.com/questions/67210340/displaynone-and-dom
          // NOTE 3: another, likely best way, to solve this is to break the accordion component into parts so that we don't have to use
          // `tablet-landscape:tw-hidden` and `tw-hidden` to render both the methodologyDesktopView and methodologyMobileView.
        }
        if (!this.chart) return;
        try {
          const jsonValues = this.extractJson();
          const kind = jsonValues.kind || "undefined";
          if (!this._data) {
            if (kind === "compliance_donut") {
              //NOTE: this must match the value set in the Elm serialization code
              this._data = compliance_donut(jsonValues);
            } else if (kind === "esg_gauge") {
              //NOTE: this must match the value set in the Elm serialization code
              this._data = esg_gauge_and_donut(jsonValues);
            } else if (kind === "esg_bars") {
              //NOTE: this must match the value set in the Elm serialization code
              this._data = esg_bars(jsonValues);
            } else if (kind === "esg_line") {
              //NOTE: this must match the value set in the Elm serialization code
              this._data = esg_line(jsonValues);
            } else if (kind === "custom") {
              this._data = jsonValues;
            }
            // ADD HERE: NEW CHART types
            else {
              this._data = jsonValues;
            }
          }

          // this.resize(); // NOTE: probably not needed
          this.chart.setOption(this._data);
        } catch (e) {
          console.error(e);
        }
      }

      resize() {
        if (debugLifecycle) {
          console.log("echarts resizing");
        }
        if (!this.chart) return;
        this.chart.resize();
      }

      extractJson() {
        if (debugLifecycle) {
          console.log("echart parse json");
        }
        const parseThis = this.getAttribute("json");
        if (!parseThis) {
          throw "invalid format: 'json' attribute should contain json data";
        }
        return JSON.parse(parseThis.replace("\n", " "));
      }
    },
  );
};

export default { init };
